import { Box, Button, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { FC, Fragment, useContext, useRef, useState } from 'react';
import { FormProvider, SubmitHandler } from 'react-hook-form';
// components
import { ArrowIcon } from 'assets/svgs';
import StepForm from '../Steps/StepForm';
import { Alert } from 'components/common/Alert';
import DialogBox from 'components/common/DialogBox';
import { BackdropLoader } from 'components/common/BackdropLoader';
import SaveAsDraftButton from 'components/common/SaveAsDraftButton';
import SaveAsDraftDialogBox from 'components/common/SaveAsDraftDialogBox';
// reducer, actions, graphql, constants, schema, context, helper, hooks
import {
  BACK_TEXT,
  SAVE_TEXT,
  HTTP_STATUS,
  INGREDIENTS_TEXT,
  EDIT_FORMULA_TEXT,
  FORMULA_FETCH_TEXT,
  FORMULA_IS_UPDATING_TEXT,
  FORMULA_ROUTE,
} from 'constants/index';
import {
  FormulaBlendInput,
  BottleCapType,
  BottleTypeEnum,
  FactBoxType,
  MiniTabFormulationType,
  ProteinBaseType,
  SweetenerNatureType,
  TabletCoatingColor,
  VeganType,
  useUpdateFormulaMutation,
  useUpdateDraftedFormulaMutation,
  CoMfgCostType,
} from 'generated/graphql';
import {
  EditFormulaFormProps,
  DosageFormulationFormType,
  AddNewFormulaFormType,
  ParamType,
  SaveFormulaAsDraftFormType,
} from 'interfaces';
import useCustomBlocker from 'hooks/useCustomBlocker';
import { ActionType } from 'reducer/addFormulaReducer';
import { FormulaContext } from 'contexts/FormulaContext';
import { formatCalcFormulaIngredients, formatCalcFormulaIngredientsWithOptionalType } from 'lib/helper';
import { flexCenterBetween } from 'styles/commonComponentStyle';
import { formatAgeGroups } from 'lib/helper';

const EditFormulaForm: FC<EditFormulaFormProps> = ({ methods, formulaMethods, loading: getLoading }) => {
  const { id: formulaId } = useParams<ParamType>();

  const navigate = useNavigate();
  const { state, dispatch } = useContext(FormulaContext);
  const { activeStep, formulation, ingredientsBlend } = state || {};

  // local state
  const [isBlocker, setIsBlocker] = useState(true);
  const [autoSaveFormula, setAutoSaveFormula] = useState(false);
  const [isOpenDraft, setIsOpenDraft] = useState(false);
  const navigateToFormulasListRef = useRef(false);

  const {
    getValues: getFormulaValues,
    formState: formulaFormState,
    handleSubmit: handleFormulaSubmit,
    setValue: setFormulaValues,
  } = formulaMethods;

  const { name: formulaNameInForm } = getFormulaValues();

  const { isDirty: isFormulaFormDirty } = formulaFormState;

  const { handleSubmit, formState, getValues } = methods;
  const { isDirty } = formState;

  const blocker = useCustomBlocker(isDirty, isFormulaFormDirty, isBlocker);

  const [updateFormula, { loading: updateLoading }] = useUpdateFormulaMutation({
    onCompleted: (res) => {
      const { updateFormula } = res || {};
      const { response } = updateFormula || {};
      const { message, status } = response || {};
      if (status === HTTP_STATUS.CREATED) {
        Alert.success(message || '');
        if (isOpenDraft) {
          setIsOpenDraft(false);
          navigate(FORMULA_ROUTE);
        }
        if (autoSaveFormula) {
          blocker?.proceed && blocker?.proceed();
        } else {
          navigate(FORMULA_ROUTE);
        }
      } else {
        Alert.warning(message || '');
      }
    },

    onError: ({ message }) => {
      Alert.error(message);
    },
  });

  const [updateDraftedFormula, { loading: updateDraftLoading }] = useUpdateDraftedFormulaMutation({
    onCompleted: (res) => {
      const { updateDraftedFormula } = res || {};
      const { response } = updateDraftedFormula || {};
      const { message, status } = response || {};
      if (status === HTTP_STATUS.CREATED) {
        Alert.success(message || '');
        if (navigateToFormulasListRef.current) {
          navigateToFormulasListRef.current = false;
          setIsOpenDraft(false);
          navigate(FORMULA_ROUTE);
          return;
        }
        if (isOpenDraft) {
          setIsOpenDraft(false);
        }
        if (autoSaveFormula) {
          blocker?.proceed && blocker?.proceed();
        }
      } else {
        Alert.warning(message || '');
      }
    },
    onError: ({ message }) => {
      Alert.error(message);
    },
  });

  // setting payload for formulaBlends
  const formulaBlends = ingredientsBlend?.map<FormulaBlendInput>((item) => {
    const { name: blendName, ingredients } = item;

    const ingredientBlends = formatCalcFormulaIngredients(ingredients);

    return {
      name: blendName,
      formulaIngredients: ingredientBlends,
    };
  });

  const onSave: SubmitHandler<AddNewFormulaFormType> = async (data) => {
    const { formulaIngredients, name } = data;
    const formatFormulaIngredients = formatCalcFormulaIngredients(formulaIngredients);
    const {
      beadlets,
      bottleCap,
      bottleType,
      capsuleType,
      coatingColor,
      coatingColor2,
      coatingColor3,
      colorsHex,
      excipientType,
      factBoxType,
      flavorSystem,
      flavorType,
      innerCapsuleType,
      miniTabFormulation,
      outerCapsuleType,
      productFormulationType,
      productSubType,
      productType,
      proteinBase,
      sweetenerSystem,
      sweetenerType,
      tabletCoating,
      tabletType,
      veganType,
      productSize,
      sandCoating,
      miscellaneousCost,
      description,

      coMfgCostType,
      coMfgCostPercentage,
      ageGroups,
    } = formulation;

    setIsBlocker(false);

    await updateFormula({
      variables: {
        updateFormulaInput: {
          id: formulaId ?? '',
          name,

          ...(coMfgCostType && { coMfgCostType: coMfgCostType as CoMfgCostType }),
          ...(coMfgCostPercentage && { coMfgCostPercentage: Number(coMfgCostPercentage) }),

          ...(bottleCap && { bottleCap: bottleCap as BottleCapType }),
          ...(bottleType && { bottleType: bottleType as BottleTypeEnum }),

          productTypeId: productType,
          productFormulationTypeId: productFormulationType?.value ?? '',
          ...(productSize?.value && { productSizeId: productSize?.value }),

          ...(ingredientsBlend?.length && { formulaBlends: formulaBlends }),
          ...(formulaIngredients?.length && { formulaIngredients: formatFormulaIngredients }),

          ...(colorsHex?.length && { colorsHex: colorsHex }),
          ...(beadlets && { beadletId: beadlets }),
          ...(productSubType && { subProductTypeId: productSubType }),
          ...(tabletType && { tabletTypeId: tabletType }),
          ...(excipientType?.value && { excipientTypeId: excipientType?.value }),

          ...(description && { description }),
          ...(miscellaneousCost && { miscellaneousCost: Number(miscellaneousCost) }),

          ...(veganType && { veganType: veganType as VeganType }),
          ...(factBoxType && { factBoxType: factBoxType as FactBoxType }),
          ...(proteinBase && { proteinBase: proteinBase as ProteinBaseType }),
          ...(miniTabFormulation && { miniTabFormulation: miniTabFormulation as MiniTabFormulationType }),

          ...(flavorSystem?.value && { flavorSystemId: flavorSystem?.value }),
          ...(flavorType && { flavorType: flavorType as SweetenerNatureType }),

          ...(sweetenerSystem?.value && { sweetenerSystemId: sweetenerSystem?.value }),
          ...(sweetenerType && { sweetenerType: sweetenerType as SweetenerNatureType }),

          ...(coatingColor && { colorCoating: coatingColor as TabletCoatingColor }),
          ...(coatingColor2 && { colorCoating2: coatingColor2 as TabletCoatingColor }),
          ...(coatingColor3 && { colorCoating3: coatingColor3 as TabletCoatingColor }),

          ...(sandCoating && { sandCoatingId: sandCoating }),
          ...(tabletCoating && { tabletCoatingId: tabletCoating }),

          ...(capsuleType && { capsuleTypeId: capsuleType }),
          ...(innerCapsuleType && { innerCapsuleTypeId: innerCapsuleType }),
          ...(outerCapsuleType && { outerCapsuleTypeId: outerCapsuleType }),
          ageGroups: formatAgeGroups(ageGroups ?? []),
        },
      },
    });
  };

  const onSaveAsDraft = () => {
    setIsOpenDraft(true);
    setIsBlocker(false);
  };

  const onSubmit: SubmitHandler<DosageFormulationFormType> = (data) => {
    dispatch({ type: ActionType.SET_FORMULATION, formulation: data });
    dispatch({ type: ActionType.SET_ACTIVE_STEP, activeStep: activeStep + 1 });
  };

  const saveFormulaAsDraftAndLeave: SubmitHandler<SaveFormulaAsDraftFormType> = (data) => {
    navigateToFormulasListRef.current = true;
    saveFormulaAsDraftAndContinue(data);
  };

  const saveFormulaAsDraftAndContinue: SubmitHandler<SaveFormulaAsDraftFormType> = async (data) => {
    setAutoSaveFormula(true);
    const { name: formulaName } = data;
    const {
      beadlets,
      bottleCap,
      bottleType,
      capsuleType,
      coatingColor,
      coatingColor2,
      coatingColor3,
      colorsHex,
      excipientType,
      factBoxType,
      flavorSystem,
      flavorType,
      innerCapsuleType,
      miniTabFormulation,
      outerCapsuleType,
      productFormulationType,
      proteinBase,
      sweetenerSystem,
      sweetenerType,
      tabletCoating,
      tabletType,
      veganType,
      productType,
      productSubType,
      productSize,
      sandCoating,
      miscellaneousCost,
      description,

      coMfgCostType,
      coMfgCostPercentage,
      ageGroups,
    } = getValues();

    const { formulaIngredients } = getFormulaValues();

    const formatFormulaIngredients = formatCalcFormulaIngredientsWithOptionalType(formulaIngredients);
    try {
      await updateDraftedFormula({
        variables: {
          updateDraftedFormulaInput: {
            id: formulaId ?? '',
            name: formulaName,

            ...(productType && { productTypeId: productType }),
            ...(productSubType && { subProductTypeId: productSubType }),
            ...(productSize?.value && { productSizeId: productSize?.value }),
            ...(productFormulationType?.value && { productFormulationTypeId: productFormulationType?.value }),

            ...(coMfgCostType && { coMfgCostType: coMfgCostType as CoMfgCostType }),
            ...(coMfgCostPercentage && { coMfgCostPercentage: Number(coMfgCostPercentage) }),

            ...(bottleCap && { bottleCap: bottleCap as BottleCapType }),
            ...(bottleType && { bottleType: bottleType as BottleTypeEnum }),

            ...(ingredientsBlend?.length && { formulaBlends: formulaBlends }),
            ...(formulaIngredients?.length && { formulaIngredients: formatFormulaIngredients }),

            ...(beadlets && { beadletId: beadlets }),
            ...(tabletType && { tabletTypeId: tabletType }),
            ...(colorsHex?.length && { colorsHex: colorsHex }),
            ...(excipientType?.value && { excipientTypeId: excipientType?.value }),

            ...(veganType && { veganType: veganType as VeganType }),
            ...(factBoxType && { factBoxType: factBoxType as FactBoxType }),
            ...(proteinBase && { proteinBase: proteinBase as ProteinBaseType }),
            ...(miniTabFormulation && { miniTabFormulation: miniTabFormulation as MiniTabFormulationType }),

            ...(flavorSystem?.value && { flavorSystemId: flavorSystem?.value }),
            ...(flavorType && { flavorType: flavorType as SweetenerNatureType }),

            ...(sweetenerSystem?.value && { sweetenerSystemId: sweetenerSystem?.value }),
            ...(sweetenerType && { sweetenerType: sweetenerType as SweetenerNatureType }),

            ...(coatingColor && { colorCoating: coatingColor as TabletCoatingColor }),
            ...(coatingColor2 && { colorCoating2: coatingColor2 as TabletCoatingColor }),
            ...(coatingColor3 && { colorCoating3: coatingColor3 as TabletCoatingColor }),

            ...(sandCoating && { sandCoatingId: sandCoating }),
            ...(tabletCoating && { tabletCoatingId: tabletCoating }),

            ...(capsuleType && { capsuleTypeId: capsuleType }),
            ...(innerCapsuleType && { innerCapsuleTypeId: innerCapsuleType }),
            ...(outerCapsuleType && { outerCapsuleTypeId: outerCapsuleType }),
            ...(miscellaneousCost && { miscellaneousCost: Number(miscellaneousCost) }),
            ...(description && { description }),

            ageGroups: formatAgeGroups(ageGroups ?? []),
          },
        },
      });

      // set values to the form
      setFormulaValues('name', formulaName);
    } catch (error) {
      console.log('error in save as draft and continue', error);
    }
  };

  const onClose = () => {
    setIsOpenDraft(false);
    setIsBlocker(true);
  };

  const loading = getLoading || updateLoading || updateDraftLoading;

  return (
    <Fragment>
      <DialogBox
        formulaName={formulaNameInForm}
        cancelNavigation={() => {
          setAutoSaveFormula(false);
          blocker?.reset && blocker?.reset();
        }}
        confirmNavigation={blocker?.proceed}
        showDialog={blocker.state === 'blocked'}
        saveAndLeaveNavigation={saveFormulaAsDraftAndContinue}
      />

      <SaveAsDraftDialogBox
        onClose={onClose}
        open={isOpenDraft}
        formulaName={formulaNameInForm}
        onSave={saveFormulaAsDraftAndContinue}
        onSaveAndLeave={saveFormulaAsDraftAndLeave}
      />

      <BackdropLoader open={loading} text={getLoading ? FORMULA_FETCH_TEXT : FORMULA_IS_UPDATING_TEXT} />
      <FormProvider {...methods}>
        <Box sx={flexCenterBetween}>
          <Typography variant="h5">{EDIT_FORMULA_TEXT}</Typography>
          {activeStep === 1 ? (
            <Box display="flex" justifyContent="space-between" gap={2}>
              <Button
                color="primary"
                variant="outlined"
                onClick={() => {
                  dispatch({ type: ActionType.SET_ACTIVE_STEP, activeStep: activeStep - 1 });
                }}>
                {BACK_TEXT}
              </Button>
              <Box>
                <SaveAsDraftButton onClick={onSaveAsDraft} loading={loading} />

                <Button
                  variant="contained"
                  color="primary"
                  disabled={loading}
                  onClick={handleFormulaSubmit(onSave)}>
                  {SAVE_TEXT}
                </Button>
              </Box>
            </Box>
          ) : (
            <Button
              variant="contained"
              color="primary"
              endIcon={<ArrowIcon />}
              onClick={handleSubmit(onSubmit)}>
              {`Choose ${INGREDIENTS_TEXT}`}
            </Button>
          )}
        </Box>
        <StepForm formulaMethods={formulaMethods} activeStep={activeStep} />
      </FormProvider>
    </Fragment>
  );
};

export default EditFormulaForm;
